import { APPROVED, YES, WORKFLOW_RERUN } from "const"
import {
    CcLibraryItemList,
    CcComplianceHistory,
} from "server/models"
import {
    LangType,
    CcLibrarySetList,
    ComplianceRevisionActiveList,
    ComplianceStateList,
    ComplianceHistoryList,
    CcApprovalLevelList,
    CcResponseList,
    UserList,
} from "client/store"
import {
    NestedHistory,
    HistoryListPdfExport,
    TableGroupedIds,
    TableNestedIds,
} from "client/components"

import * as Const from "client/components/pdfmake/template/const"

const historyMargin = [0, 0, 0, 60]
const assignedUserMargin = [20, 0, 0, 5]
const statusMargin = [20, 0, 0, 20]

const ComplianceSetsPage = (
    setList: CcLibrarySetList,
    setIds: readonly number[],
    itemList: CcLibraryItemList,
    nested: { [setId: number]: TableGroupedIds },
    revisionList: ComplianceRevisionActiveList,
    complianceStateList: ComplianceStateList,
    complianceHistoryList: ComplianceHistoryList,
    complianceHistoryNestedList: { [itemId: number]: NestedHistory[] },

    setImageBase64: { [setId: number]: string },

    levelList: CcApprovalLevelList,
    levelIds: number[],
    responseList: CcResponseList,
    userList: UserList,
    lang: LangType,
) => {
    let content: any[] = []
    let stack: any[] = []

    const downwardArrow = (multiply: number) => ({
        width: 18 * multiply,
        image: "downwardArrow",
        fit: [10, 10],
        alignment: "right",
        margin: [0, 0, 3, 0],
    })

    let pageBreak = false

    const prepareNestedItems = (nestedItems: readonly TableNestedIds[], parentItems: string[]) => {
        nestedItems.forEach(({ id, nestedIds }) => {
            const item = itemList[Number(id)]
            const itemStack: any[] = []

            if (parentItems && parentItems.length) {
                parentItems.forEach((parentItem, index) => {
                    const columns: any[] = []
                    if (index > 0) {
                        columns.push(downwardArrow(index))
                    }
                    columns.push({
                        width: "*",
                        text: parentItem,
                    })
                    itemStack.push({
                        columns,
                        margin: [0, 5, 0, 0],
                    })
                })

                itemStack.push({
                    columns: [
                        downwardArrow(parentItems.length),
                        {
                            width: "*",
                            text: item.compliantItem,
                        },
                    ],
                    margin: [0, 5, 0, 0],
                })
            } else {
                itemStack.push({ text: item.compliantItem })
            }

            stack.push(
                {
                    style: Const.PARAGRAPH,
                    bold: true,
                    text: `${lang.item} ${item.publicId}`,
                    margin: [0, 5, 0, 8],
                    pageBreak: pageBreak && "before",
                },
                {
                    style: Const.PARAGRAPH,
                    stack: itemStack,
                    margin: [0, 0, 0, 15],
                    alignment: "justify",
                },
            )

            // compliance
            const itemState = complianceStateList[item.id]
            const itemHistory = complianceHistoryList[item.id]

            if (itemState && itemHistory && itemHistory.length) {
                pageBreak = true

                let lastHistory: CcComplianceHistory = null

                // get the last history row made by user, not by 'system'
                for (let i = itemHistory.length - 1; i >= 0; i--) {
                    const history = itemHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage) {
                        lastHistory = history
                        break
                    }
                }

                // same as in compliance list columns
                const level = itemState ? levelList[itemState.levelId] : null
                const verifier = itemState ? userList[itemState.verifierId] : null

                if (lastHistory && lastHistory.nextStatus === APPROVED) {
                    const response = responseList[lastHistory.responseId]
                    stack.push({
                        text: `${lang.status}: ${response && response.approved === YES ? lang.approved : lang.notApproved}`,
                        margin: statusMargin,
                    })
                } else if (level) {
                    const firstLevel = level.id === levelIds[0]
                    const verifierName = verifier ? `${verifier.firstName} ${verifier.lastName}` : lang.notAssigned
                    stack.push(
                        {
                            text: `${level.name}: ${verifierName}`,
                            margin: assignedUserMargin,
                        },
                        {
                            text: `${lang.status}: ${firstLevel ? lang.open : lang.verification}`,
                            margin: statusMargin,
                        },
                    )
                } else if (!level && lastHistory) { // approval level was removed or not set
                    stack.push(
                        {
                            text: `${lang.status}: ${lang.verification}`,
                            margin: statusMargin,
                        },
                    )
                } else if (!level && !lastHistory) { // no any data
                    stack.push(
                        {
                            text: `${lang.status}: ${lang.open}`,
                            margin: statusMargin,
                        },
                    )
                }

                // compliance history
                const historyNested = complianceHistoryNestedList[item.id]
                if (historyNested.length > 0) {
                    // table wrapper around an item history is required to keep the page structure
                    stack.push({
                        layout: Const.ITEM_HISTORY_TABLE_LAYOUT,
                        table: {
                            widths: ["*"],
                            body: [
                                [{ stack: HistoryListPdfExport(historyNested, userList, levelList, responseList, lang) }],
                            ],
                        },
                        margin: historyMargin,
                    })
                }
            } else {
                pageBreak = false
            }

            const newParentItems = parentItems ? parentItems.slice() : []
            newParentItems.push(item.compliantItem)
            prepareNestedItems(nestedIds, newParentItems)
        })
    }

    const prepareNestedGroups = (nestedGroups: readonly TableGroupedIds[]) => {
        nestedGroups.forEach(({ groupData, nestedIds, nestedGroupedIds }) => {
            if (groupData) {
                stack.push(
                    {
                        bold: true,
                        fontSize: 12,
                        margin: [0, 5, 0, 8],
                        text: groupData as string,
                        ...Const.TOC_CC_ITEM_BASE,
                        pageBreak: pageBreak && "before",
                    },
                )
                pageBreak = false
            }

            prepareNestedItems(nestedIds, null)
            prepareNestedGroups(nestedGroupedIds)
        })
    }


    setIds.forEach((setId, index) => {
        const set = setList[setId]
        const revision = revisionList[setId]

        stack = [] // clear stack
        pageBreak = false

        const setNested = nested[setId]
        prepareNestedItems(setNested.nestedIds, null)
        prepareNestedGroups(setNested.nestedGroupedIds)

        const tocItemIndex = index + 1
        content = content.concat([
            {
                pageBreak: "before",
                columns: [
                    {
                        width: "*",
                        style: Const.HEADER,
                        text: `2.${tocItemIndex}  ${set.name}  -  ${set.publicId}`,
                        ...Const.TOC_ITEM_BASE,
                        tocMargin: [0, index === 0 ? 0 : 8, 0, 0],
                    },
                    {
                        width: "20%",
                        stack: [{
                            image: setImageBase64[set.id],
                            fit: [90, 90],
                            alignment: "right",
                        }],
                    },
                ],
            },
            {
                style: Const.SUB_HEADER,
                text: `2.${tocItemIndex}.1   ${lang.complianceSetDescription}`,
                ...Const.TOC_ITEM_BASE,
            },
            {
                style: Const.PARAGRAPH,
                text: set.description,
                margin: [0, 5, 0, 10],
            },
            {
                style: Const.SUB_HEADER,
                text: `2.${tocItemIndex}.2   ${lang.compliantItems}  -  ${lang.revision}:  ${revision.name}`,
                ...Const.TOC_ITEM_BASE,
            },
            {
                stack,
            },
        ])
    })

    return content
}
export default ComplianceSetsPage
